import { useEffect, useState } from "react";
import { API } from "aws-amplify";

import { QueryStatus } from "../../app/types";

export type TrainingStage = {
  id: string;
  code: string;
  displayName: string;
  yearLabelPrefix: string;
  minYear: number;
  maxYear: number;
  sortOrder: number;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
};

export const fetchTrainingStages = async (): Promise<TrainingStage[]> =>
  (await API.get("simpl-api", "training-stages", {})) as TrainingStage[];

export type YearInStageUpdate = {
  membershipId: string;
  newValue: number;
};

export type YearInStageUpdateResponse = {
  updated: number;
  skipped: { membershipId: string; reason: string }[];
};

// WAF blocks request bodies > 8KB (api.simpl.org SizeRestrictions_BODY); each
// update is ~70 bytes, so keep chunks well under that (~50 => ~3.5KB).
const YEAR_IN_STAGE_CHUNK_SIZE = 50;

const postYearInStage = async (
  updates: YearInStageUpdate[]
): Promise<YearInStageUpdateResponse> =>
  (await API.post("simpl-api", "update-year-in-stage", {
    body: { updates }
  })) as YearInStageUpdateResponse;

export const updateYearInStage = async (
  updates: YearInStageUpdate[]
): Promise<YearInStageUpdateResponse> => {
  if (updates.length <= YEAR_IN_STAGE_CHUNK_SIZE) {
    return postYearInStage(updates);
  }

  // Chunk sequentially (single Turso writer) and aggregate updated/skipped.
  const aggregate: YearInStageUpdateResponse = { updated: 0, skipped: [] };
  for (let i = 0; i < updates.length; i += YEAR_IN_STAGE_CHUNK_SIZE) {
    const res = await postYearInStage(
      updates.slice(i, i + YEAR_IN_STAGE_CHUNK_SIZE)
    );
    aggregate.updated += res.updated;
    aggregate.skipped.push(...(res.skipped ?? []));
  }
  return aggregate;
};

// Returns the user's active Trainee membership (end_date null) or undefined.
const findActiveTraineeMembership = (user: any): any | undefined =>
  user?.programMemberships?.items?.find(
    (pm: any) => pm && !pm.endDate && pm.programRole === "Trainee"
  );

// Whether a user is eligible for a +N Year bulk action. Eligible when they have
// an active Trainee membership on a program with a known training_stage AND at
// least one of (user.pgy, pm.year_in_stage) is set.
export const canBumpUser = (
  user: any,
  trainingStages: TrainingStage[]
): boolean => {
  const m = findActiveTraineeMembership(user);
  if (!m) return false;
  const stage = trainingStages.find(
    (s) => s.id === m.program?.trainingStageId
  );
  if (!stage) return false;
  return user?.pgy != null || m.yearInStage != null;
};

// Per-user plan describing exactly what to write for a +delta bulk action.
// - GME/UME: only year_in_stage is written (update-year-in-stage syncs user.pgy).
//   Base = year_in_stage if set, else user.pgy.
// - FELLOWSHIP: year_in_stage and pgy are independent. Each is bumped only if
//   currently set.
export type PgyBumpPlan = {
  userId: string;
  membershipId: string;
  stageCode: string;
  newYearInStage: number | null;
  newPgy: number | null;
};

export const getPgyBumpPlan = (
  user: any,
  trainingStages: TrainingStage[],
  delta: number
): PgyBumpPlan | null => {
  const m = findActiveTraineeMembership(user);
  if (!m) return null;
  const stage = trainingStages.find(
    (s) => s.id === m.program?.trainingStageId
  );
  if (!stage) return null;

  const currentYear: number | null =
    m.yearInStage != null ? Number(m.yearInStage) : null;
  const currentPgy: number | null = user?.pgy != null ? Number(user.pgy) : null;
  if (currentYear == null && currentPgy == null) return null;

  if (stage.code === "FELLOWSHIP") {
    return {
      userId: user.id,
      membershipId: m.id,
      stageCode: stage.code,
      newYearInStage: currentYear != null ? currentYear + delta : null,
      newPgy: currentPgy != null ? currentPgy + delta : null
    };
  }

  const base = currentYear != null ? currentYear : (currentPgy as number);
  return {
    userId: user.id,
    membershipId: m.id,
    stageCode: stage.code,
    newYearInStage: base + delta,
    newPgy: null
  };
};

export type ApplyPgyBumpsResult = {
  yearInStageUpdated: number;
  yearInStageSkipped: { membershipId: string; reason: string }[];
  pgyUpdated: number;
  pgyFailed: { userId: string; error: string }[];
  totalUsers: number;
};

// Executes the plans: a single bulk update-year-in-stage call plus N parallel
// update-user calls (one per Fellowship user with a pgy bump). Caller supplies
// the updateUser function to avoid a circular import between trainingStagesAPI
// and usersAPI.
export const applyPgyBumps = async (
  plans: PgyBumpPlan[],
  updateUser: (input: {
    id: string;
    pgy: number;
  }) => Promise<unknown | null>
): Promise<ApplyPgyBumpsResult> => {
  const yearInStageUpdates: YearInStageUpdate[] = plans
    .filter((p) => p.newYearInStage != null)
    .map((p) => ({
      membershipId: p.membershipId,
      newValue: p.newYearInStage as number
    }));
  const pgyUpdates = plans.filter((p) => p.newPgy != null);

  const yearPromise: Promise<YearInStageUpdateResponse> =
    yearInStageUpdates.length > 0
      ? updateYearInStage(yearInStageUpdates)
      : Promise.resolve({ updated: 0, skipped: [] });

  const pgyPromises = pgyUpdates.map((p) =>
    updateUser({ id: p.userId, pgy: p.newPgy as number })
      .then(() => ({ ok: true as const, userId: p.userId }))
      .catch((err: any) => ({
        ok: false as const,
        userId: p.userId,
        error: String(err?.message ?? err)
      }))
  );

  const [yearResult, ...pgyResults] = await Promise.all([
    yearPromise,
    ...pgyPromises
  ]);

  return {
    yearInStageUpdated: yearResult.updated,
    yearInStageSkipped: yearResult.skipped ?? [],
    pgyUpdated: pgyResults.filter((r) => r.ok).length,
    pgyFailed: pgyResults
      .filter((r): r is { ok: false; userId: string; error: string } => !r.ok)
      .map(({ userId, error }) => ({ userId, error })),
    totalUsers: plans.length
  };
};

export const useTrainingStages = () => {
  const [trainingStages, setTrainingStages] = useState<TrainingStage[]>([]);
  const [status, setStatus] = useState<QueryStatus>("idle");
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setStatus("loading");
    setError(null);

    fetchTrainingStages()
      .then((rows) => {
        if (cancelled) return;
        setTrainingStages(rows);
        setStatus("succeeded");
      })
      .catch((err) => {
        if (cancelled) return;
        console.error("Error fetching training stages:", err);
        setError(`${err}`);
        setStatus("failed");
      });

    return () => {
      cancelled = true;
    };
  }, []);

  return { trainingStages, status, error };
};
